// © Microsoft Corporation. All rights reserved.
import { AudioDeviceInfo, VideoDeviceInfo, RemoteVideoStream } from '@azure/communication-calling';
import { CommunicationIdentifierKind } from '@azure/communication-common';
import { CommunicationIdentifier, isCommunicationUserIdentifier } from '@azure/communication-common';
import { CommunicationUserToken } from '@azure/communication-identity';
import preval from 'preval.macro';
import { ChatMessage } from '@azure/communication-chat';
import { ClientChatMessage } from '../core/reducers/MessagesReducer';
import { Constants, URLOptionsConstants } from '../core/constants';

var ChatThreadID: string = "";
export const utils = {
    getAppServiceUrl: (): string => {
        return window.location.origin;
    },
    getTokenForUser: async (): Promise<CommunicationUserToken> => {
        const response = await fetch('/token');
        if (response.ok) {
            return response.json();
        }
        throw new Error('Invalid token response');
    },
    getRefreshedTokenForUser: async (identity: string): Promise<string> => {
        const response = await fetch(`/refreshToken/${identity}`);
        if (response.ok) {
            const content = await response.json();
            return content.token;
        }
        throw new Error('Invalid token response');
    },
    isSelectedAudioDeviceInList(selected: AudioDeviceInfo, list: AudioDeviceInfo[]): boolean {
        return list.filter((item) => item.name === selected.name).length > 0;
    },
    isSelectedVideoDeviceInList(selected: VideoDeviceInfo, list: VideoDeviceInfo[]): boolean {
        return list.filter((item) => item.name === selected.name).length > 0;
    },
    isMobileSession(): boolean {
        return window.navigator.userAgent.match(/(iPad|iPhone|iPod|Android|webOS|BlackBerry|Windows Phone)/g)
            ? true
            : false;
    },
    isSmallScreen(): boolean {
        return window.innerWidth < 700 || window.innerHeight < 400;
    },
    isUnsupportedBrowser(): boolean {
        return window.navigator.userAgent.match(/(Firefox)/g) ? true : false;
    },
    getId: (identifier: CommunicationIdentifierKind): string => {
        switch (identifier.kind) {
            case 'communicationUser':
                return identifier.communicationUserId;
            case 'phoneNumber':
                return identifier.phoneNumber;
            case 'microsoftTeamsUser':
                return identifier.microsoftTeamsUserId;
            case 'unknown':
                return identifier.id;
            default:
                return "";
        }
    },
    getStreamId: (userId: string, stream: RemoteVideoStream): string => {
        return `${userId}-${stream.id}-${stream.mediaStreamType}`;
    },
    getPinFromLabel: (label: string): string => {
        const parts = label.split("_");
        if (parts.length > 1)
            return parts[1];
        else
            return label;
    },
    getUsernameFromLabel: (label: string): string => {
        const parts = label.split("_");

        if (parts.length > 1)
            return parts[0];
        else if (IsProctorMode())
            return GetUsernameLabel(label);
        else
            return label;
    },
    isOnIphoneAndNotSafari(): boolean {
        const userAgent = navigator.userAgent;
        // Chrome uses 'CriOS' in user agent string and Firefox uses 'FxiOS' in user agent string.
        if (userAgent.includes('iPhone') && (userAgent.includes('FxiOS') || userAgent.includes('CriOS'))) {
            return true;
        }
        return false;
    },
    isSafari(): boolean {
        // https://stackoverflow.com/questions/7944460/detect-safari-browser
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        console.log('UserAgent:- is safari:' + isSafari);
        return isSafari;
    },
    getBuildTime: (): string => {
        const dateTimeStamp = preval`module.exports = new Date().toLocaleString();`;
        return dateTimeStamp;
    },
    CreateChatThread: async (): Promise<string> => {
        let createThreadRequestOptions = { method: 'POST' };
        const response = await fetch('/createThread', createThreadRequestOptions);
        if (response.ok) {
            const tt = await response.json();
            ChatThreadID = tt.chatid
            return ChatThreadID;
        }
        else {
            return "Error";
        }
    }
};

export const convertToClientChatMessage = (chatMessage: ChatMessage, clientMessageId?: string): ClientChatMessage => {
    return {
        content: { message: chatMessage.content?.message },
        clientMessageId: clientMessageId,
        sender: chatMessage.sender,
        senderDisplayName: chatMessage.senderDisplayName,
        createdOn: chatMessage.createdOn,
        id: chatMessage.id
    };
};
export const isUserMatchingIdentity = (user: CommunicationIdentifier, communicationUserId: string): boolean => {
    return isCommunicationUserIdentifier(user) && user.communicationUserId === communicationUserId;
};

export const ChatCommunicationIdentifierId = (user: CommunicationIdentifier): string => {
    if (isCommunicationUserIdentifier(user))
        return user.communicationUserId;
    else
        return '';
};

export const compareMessages = (firstMessage: ClientChatMessage, secondMessage: ClientChatMessage) => {
    if (firstMessage.createdOn === undefined) return 1;
    if (secondMessage.createdOn === undefined) return -1;
    const firstDate = new Date(firstMessage.createdOn).getTime();
    const secondDate = new Date(secondMessage.createdOn).getTime();
    return firstDate - secondDate;
};
export const createNewClientChatMessage = (
    userId: string,
    displayName: string,
    clientMessageId: string,
    message: string
): ClientChatMessage => {
    return {
        content: { message: message },
        clientMessageId: clientMessageId,
        sender: { communicationUserId: userId, kind: 'communicationUser' },
        senderDisplayName: displayName,
        createdOn: new Date()
    };
};

const IsHiddenUser = (user: string) => {
    if (user.includes(Constants.HIDDEN_USER_PREFIX))
        return true;
    else
        return false;
}

const IsSecondStreamUser = (user: string) => {
    if (user.includes(Constants.Secondary_USER_PREFIX))
        return true;
    else
        return false;
}

const ShouldUserBeVisible = (user: string) => {
    if (IsHiddenUser(user))
        return false;
    else if (IsCandidateMode() && IsSecondaryStreamUser(user))
        return false;
    else if (IsSecondaryStream())
        return false;
    else
        return true;
}

const GetUsername = () => {
    let urlParams = new URLSearchParams(window.location.search.substring(1));
    let randomUserName = 'user' + Math.ceil(Math.random() * 1000);
    let observerUserName = 'Observer' + Math.ceil(Math.random() * 1000)
    let uriusername = urlParams.get('username') ?? randomUserName;

    if (IsObserverMode())
        return `${Constants.HIDDEN_USER_PREFIX}${observerUserName}`;
    else if (IsCentralScreenMode())
        return `${Constants.HIDDEN_USER_PREFIX}${uriusername}`;  
    else if (IsProctorMode())
        return `${Constants.HIDDEN_USER_PREFIX}${uriusername}`;
    else if (IsSecondaryStream())
        return `${Constants.Secondary_USER_PREFIX}${Math.ceil(Math.random() * 1000)}`;
    else
        return uriusername;
}

const HasCallMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    const islocked = urlParams.get('islocked');
    let res = false;
    if (callMode || islocked) {
        res = true;
    }
    return res;
};

const IsCandidateMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const uriislocked = urlParams.get('islocked');
    const isLocked = (uriislocked != null && uriislocked === "true");
    const callMode = urlParams.get('callMode');
    let res = false;

    if (!callMode && isLocked) {
        res = true;
    }
    else if (callMode == '1') {
        res = true;
    }

    return res;
};

const IsInterlocutorMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    let res = false;
    if (callMode == '2') {
        res = true;
    }
    return res;
};

const IsObserverMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    let res = false;
    if (callMode == '-1') {
        res = true;
    }
    return res;
};

const IsHiddenMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    let res = false;
    if (callMode == '-1' || callMode == '3') {
        res = true;
    }
    return res;
};

const IsProctorMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    let res = false;
    if (callMode == '4') {
        res = true;
    }
    return res;
};

const IsCentralScreenMode = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    let res = false;
    if (callMode == '3') {
        res = true;
    }
    return res;
}

const IsSecondaryStream = () => {
    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const callMode = urlParams.get('callMode');
    let res = false;
    if (callMode == '5') {
        res = true;
    }
    return res;
};
const IsSecondaryStreamUser = (user: string | undefined) => {
    if (user?.includes(Constants.Secondary_USER_PREFIX))
        return true;
    else
        return false;
}

const GetUsernameLabel = (user: string) => {
    return IsHiddenUser(user) ? user.slice(6) : user;

}
const ShouldAutoStart = (hasvideoDeviceInfo: boolean)=>
{
    if ((IsCandidateMode()) && hasvideoDeviceInfo)
        return true;
    else if ((IsHiddenMode() || IsProctorMode()))
        return true;
    else if (IsSecondaryStream() && hasvideoDeviceInfo)
        return true;
    else return false;

}
const ShouldUserBeMuted = () => {
    if (IsCentralScreenMode() || IsSecondaryStream())
        return true;
    else if (IsHiddenMode())
        return false;
    else
        return false;

}
const ShouldShowLabel = (isInner: boolean, user: string | undefined) => {
    if (IsSecondaryStreamUser(user))
        return false;
    else if (isInner && (IsInterlocutorMode() || IsProctorMode()))
        return false;
    else if (!isInner && !IsInterlocutorMode() && !IsProctorMode())
        return false;
    else
        return true;
}
const IsProctorCameraEnabled = ()=> {

    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const ProctorCamera = urlParams.get(URLOptionsConstants.PROCTORCAMERA);

    if (ProctorCamera)
        return ProctorCamera.toLowerCase() === "true" ? true : false;

    return false;
}

export {
    IsHiddenUser,
    IsSecondStreamUser,
    ShouldUserBeVisible,
    GetUsername,
    IsCandidateMode,
    IsInterlocutorMode,
    IsObserverMode,
    IsHiddenMode,
    HasCallMode,
    IsProctorMode,
    GetUsernameLabel,
    IsCentralScreenMode,
    IsSecondaryStream,
    IsSecondaryStreamUser,
    ShouldAutoStart,
    ShouldUserBeMuted,
    ShouldShowLabel,
    IsProctorCameraEnabled
    
};

export enum UserCommand {
    Response = 0,
    Connect = 1,
    Disconnect = 2,
    SendMessage = 3,
    ToggleMicrophone = 4,
    ToggleCamera = 5,
    IncreaseVolume = 6,
    DecreaseVolume = 7,
    RequestMicrophoneState = 9,
    RequestCameraState = 10,
    ToggleScreenSharing = 11,
    RequestSettings = 12,
    ResponseSettings = 13,
    TryUpdateCandidateCamera = 14,
    TryUpdateCandidateMicrophone = 15,
    RefreshCandidatePage=16

}

export enum DeviceType {
    Camera = 0,
    Microphone = 1
}